import { lazy, Suspense, useEffect, useState } from "react";
import { LogIn, LogOut, Menu, Settings, User, X } from "lucide-react";
import { NavLink, Route, Routes, Navigate, useLocation, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import FormatterPage from "./pages/FormatterPage";
import ExamplesListPage from "./pages/ExamplesListPage";
import { useAuth } from "./lib/auth-context";
import ExampleFormatterPage from "./pages/ExampleDetailPage";
import NotFoundPage from "./pages/NotFoundPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import FaqPage from "./pages/FaqPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AboutPage from "./pages/AboutPage";
import PricingPage from "./pages/PricingPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import StatusPage from "./pages/StatusPage";
import { Button } from "./components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import SeoHead from "./seo/SeoHead";
import { resolveSeoMetaForPath } from "./seo/routes";
import CookieConsent from "./components/layout/CookieConsent";
import AppFooter from "./components/layout/AppFooter";
import { applyAnalyticsConsent, trackPageView } from "./lib/analytics";
import { USER_ROLES } from "@textformatter/shared";
import {
	getCookieConsent,
	setCookieConsent,
	type CookieConsent as CookieConsentType,
} from "./lib/cookie-consent";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ApiKeyDetailsPage = lazy(() => import("./pages/ApiKeyDetailsPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ResendConfirmationPage = lazy(() => import("./pages/ResendConfirmationPage"));
const ConfirmEmailPage = lazy(() => import("./pages/ConfirmEmailPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

export default function App() {
	const { user, loading, logout } = useAuth();
	const authResolved = !loading;
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [consentReady, setConsentReady] = useState(false);
	const [cookieConsent, setCookieConsentState] = useState<CookieConsentType | null>(null);
	const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
	const [pendingAnalyticsEnabled, setPendingAnalyticsEnabled] = useState(false);
	const location = useLocation();
	const seoMeta = resolveSeoMetaForPath(location.pathname, import.meta.env.VITE_SITE_URL);
	const mobileMenuId = "mobile-main-nav";

	useEffect(() => {
		applyAnalyticsConsent(cookieConsent);
	}, [cookieConsent]);

	useEffect(() => {
		if (cookieConsent !== "accepted") return;
		trackPageView(location.pathname + location.search);
	}, [cookieConsent, location.pathname, location.search]);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
	}, [location.pathname, location.search]);

	useEffect(() => {
		setCookieConsentState(getCookieConsent());
		setConsentReady(true);
	}, []);

	useEffect(() => {
		if (!cookieSettingsOpen) return;
		setPendingAnalyticsEnabled(cookieConsent === "accepted");
	}, [cookieSettingsOpen, cookieConsent]);

	useEffect(() => {
		function onKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") {
				setMobileMenuOpen(false);
			}
		}
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, []);

	function updateCookieConsent(consent: CookieConsentType) {
		applyAnalyticsConsent(consent);
		setCookieConsent(consent);
		setCookieConsentState(consent);
		if (consent === "accepted") {
			trackPageView(location.pathname + location.search);
		}
	}

	function navItemClass(isActive: boolean) {
		return `rounded-full border px-4 py-2 transition-colors ${
			isActive
				? "border-ink bg-ink text-white hover:bg-slate-900"
				: "border-slate-200 bg-white text-ink shadow-sm hover:border-slate-300 hover:bg-slate-50"
		}`;
	}

	function accountTriggerClass() {
		return "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-ink shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 cursor-pointer";
	}

	function loginButtonClass(isActive: boolean) {
		return `inline-flex items-center gap-2 rounded-full border px-4 py-2 shadow-sm transition-colors ${
			isActive
				? "border-ink bg-ink text-white hover:bg-slate-900"
				: "border-slate-200 bg-white text-ink hover:border-slate-300 hover:bg-slate-50"
		}`;
	}

	function getAccountLabel(email: string) {
		const local = email.split("@")[0]?.trim() || "Account";
		if (local.length <= 8) return local;
		return `${local.slice(0, 8)}...`;
	}

	return (
		<div className="flex min-h-screen flex-col">
			<SeoHead meta={seoMeta} />
			<Toaster position="bottom-right" richColors />
			<header className="border-b border-slate-200 bg-white/80 backdrop-blur">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
					<div className="flex items-center gap-4">
						<div>
							<p className="text-xs uppercase tracking-[0.3em] text-slate-500">
								Text formatting tools
							</p>
							<NavLink
								to="/"
								className="font-['Fraunces'] text-xl font-bold tracking-tight text-ink"
							>
								Text Formatter
							</NavLink>
						</div>
					</div>
					<nav
						className={`hidden items-center gap-3 text-sm font-semibold transition-all duration-800 md:flex ${
							authResolved ? "opacity-100" : "opacity-0"
						}`}
					>
						{authResolved && (
							<>
								<NavLink to="/examples" className={({ isActive }) => navItemClass(isActive)}>
									Examples
								</NavLink>
								<NavLink to="/pricing" className={({ isActive }) => navItemClass(isActive)}>
									Pricing
								</NavLink>
								<NavLink to="/blog" className={({ isActive }) => navItemClass(isActive)}>
									Blog
								</NavLink>
								{user ? (
									<>
										<NavLink to="/dashboard" className={({ isActive }) => navItemClass(isActive)}>
											Dashboard
										</NavLink>
										{user.role === USER_ROLES.ADMIN ? (
											<NavLink
												to="/dashboard/admin"
												className={({ isActive }) => navItemClass(isActive)}
											>
												Admin
											</NavLink>
										) : null}
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<button type="button" className={accountTriggerClass()}>
													<User className="h-4 w-4" />
													{getAccountLabel(user.email)}
												</button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="w-44">
												<DropdownMenuItem asChild>
													<Link to="/dashboard/settings" className="cursor-pointer">
														<Settings className="mr-2 h-4 w-4" />
														Settings
													</Link>
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem
													onSelect={(event) => {
														event.preventDefault();
														void logout();
													}}
													className="text-rose-700 focus:text-rose-700 cursor-pointer"
												>
													<LogOut className="mr-2 h-4 w-4" />
													Logout
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</>
								) : (
									<NavLink to="/login" className={({ isActive }) => loginButtonClass(isActive)}>
										<LogIn className="h-4 w-4" />
										LogIn
									</NavLink>
								)}
							</>
						)}
					</nav>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="md:hidden"
						aria-label="Toggle navigation menu"
						aria-expanded={mobileMenuOpen}
						aria-controls={mobileMenuId}
						onClick={() => setMobileMenuOpen((prev) => !prev)}
					>
						{mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
					</Button>
				</div>
				<div
					id={mobileMenuId}
					className={`overflow-hidden border-t border-slate-200 bg-white px-6 transition-all duration-300 ease-out md:hidden ${
						mobileMenuOpen
							? "max-h-80 translate-y-0 pb-4 pt-3 opacity-100"
							: "max-h-0 -translate-y-1 pb-0 pt-0 opacity-0"
					}`}
				>
					<div
						className={`transition-transform duration-300 ease-out ${
							mobileMenuOpen ? "translate-y-0" : "-translate-y-1"
						}`}
					>
						<nav
							className={`flex flex-col gap-2 text-sm font-semibold transition-all duration-300 ${
								authResolved ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"
							}`}
						>
							{authResolved && (
								<>
									<NavLink
										to="/examples"
										className={({ isActive }) => navItemClass(isActive)}
										onClick={() => setMobileMenuOpen(false)}
									>
										Examples
									</NavLink>
									<NavLink
										to="/pricing"
										className={({ isActive }) => navItemClass(isActive)}
										onClick={() => setMobileMenuOpen(false)}
									>
										Pricing
									</NavLink>
									<NavLink
										to="/blog"
										className={({ isActive }) => navItemClass(isActive)}
										onClick={() => setMobileMenuOpen(false)}
									>
										Blog
									</NavLink>
									{user ? (
										<>
											<NavLink
												to="/dashboard"
												className={({ isActive }) => navItemClass(isActive)}
												onClick={() => setMobileMenuOpen(false)}
											>
												Dashboard
											</NavLink>
											{user.role === USER_ROLES.ADMIN ? (
												<NavLink
													to="/dashboard/admin"
													className={({ isActive }) => navItemClass(isActive)}
													onClick={() => setMobileMenuOpen(false)}
												>
													Admin
												</NavLink>
											) : null}
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<button type="button" className={accountTriggerClass()}>
														<User className="h-4 w-4" />
														{getAccountLabel(user.email)}
													</button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="start" className="w-56">
													<DropdownMenuItem asChild>
														<Link
															to="/dashboard/settings"
															onClick={() => setMobileMenuOpen(false)}
															className="min-h-11 py-2.5"
														>
															<Settings className="mr-2 h-4 w-4" />
															Settings
														</Link>
													</DropdownMenuItem>
													<DropdownMenuSeparator />
													<DropdownMenuItem
														onSelect={(event) => {
															event.preventDefault();
															setMobileMenuOpen(false);
															void logout();
														}}
														className="min-h-11 py-2.5 text-rose-700 focus:text-rose-700"
													>
														Logout
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</>
									) : (
										<NavLink
											to="/login"
											className={({ isActive }) => loginButtonClass(isActive)}
											onClick={() => setMobileMenuOpen(false)}
										>
											<LogIn className="h-4 w-4" />
											LogIn
										</NavLink>
									)}
								</>
							)}
						</nav>
					</div>
				</div>
			</header>

			<main className="flex-1">
				<Suspense fallback={null}>
					<Routes>
						<Route path="/" element={<FormatterPage />} />
						<Route path="/examples" element={<ExamplesListPage />} />
						<Route path="/format/:slug" element={<ExampleFormatterPage />} />
						<Route
							path="/dashboard"
							element={loading ? null : user ? <DashboardPage /> : <Navigate to="/login" />}
						/>
						<Route
							path="/dashboard/api-key/:id"
							element={loading ? null : user ? <ApiKeyDetailsPage /> : <Navigate to="/login" />}
						/>
						<Route
							path="/dashboard/payment"
							element={loading ? null : user ? <PaymentPage /> : <Navigate to="/login" />}
						/>
						<Route
							path="/dashboard/settings"
							element={loading ? null : user ? <SettingsPage /> : <Navigate to="/login" />}
						/>
						<Route
							path="/dashboard/admin"
							element={
								loading ? null : user ? (
									user.role === USER_ROLES.ADMIN ? (
										<AdminDashboardPage />
									) : (
										<Navigate to="/dashboard" replace />
									)
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						<Route
							path="/dashboard/admin/api-key/:id"
							element={
								loading ? null : user ? (
									user.role === USER_ROLES.ADMIN ? (
										<ApiKeyDetailsPage />
									) : (
										<Navigate to="/dashboard" replace />
									)
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						<Route path="/login" element={<LoginPage />} />
						<Route path="/register" element={<RegisterPage />} />
						<Route path="/resend-confirmation" element={<ResendConfirmationPage />} />
						<Route path="/confirm-email" element={<ConfirmEmailPage />} />
						<Route path="/forgot-password" element={<ForgotPasswordPage />} />
						<Route path="/reset-password" element={<ResetPasswordPage />} />
						<Route path="/faq" element={<FaqPage />} />
						<Route path="/blog" element={<BlogPage />} />
						<Route path="/blog/:slug" element={<BlogDetailPage />} />
						<Route path="/about" element={<AboutPage />} />
						<Route path="/pricing" element={<PricingPage />} />
						<Route path="/status" element={<StatusPage />} />
						<Route path="/contact" element={<ContactPage />} />
						<Route path="/terms" element={<TermsPage />} />
						<Route path="/privacy" element={<PrivacyPage />} />

						<Route path="*" element={<NotFoundPage />} />
					</Routes>
				</Suspense>
			</main>

			<AppFooter onOpenCookieSettings={() => setCookieSettingsOpen(true)} />
			<CookieConsent
				consentReady={consentReady}
				cookieConsent={cookieConsent}
				cookieSettingsOpen={cookieSettingsOpen}
				pendingAnalyticsEnabled={pendingAnalyticsEnabled}
				onOpenCookieSettings={setCookieSettingsOpen}
				onPendingAnalyticsEnabledChange={setPendingAnalyticsEnabled}
				onAccept={() => updateCookieConsent("accepted")}
				onReject={() => updateCookieConsent("rejected")}
				onCancelSettings={() => setCookieSettingsOpen(false)}
				onConfirmSettings={() => {
					updateCookieConsent(pendingAnalyticsEnabled ? "accepted" : "rejected");
					setCookieSettingsOpen(false);
					toast.success("Cookie preferences saved");
				}}
			/>
		</div>
	);
}
